/**
 * Supplier Type Definitions
 * Represents suppliers and ingredients in the halal supply chain
 */

import type { Address, ContactPerson } from './organization';

/**
 * Supplier types
 */
export enum SupplierType {
  INGREDIENTS = 'INGREDIENTS',         // Raw materials/ingredients
  PACKAGING = 'PACKAGING',             // Packaging materials
  EQUIPMENT = 'EQUIPMENT',             // Equipment suppliers
  SERVICES = 'SERVICES',               // Service providers (cleaning, logistics, etc.)
}

/**
 * Supplier status
 */
export enum SupplierStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  SUSPENDED = 'SUSPENDED',
}

/**
 * Halal certification status
 */
export enum HalalCertificationStatus {
  CERTIFIED = 'CERTIFIED',               // Has valid halal certificate
  NOT_CERTIFIED = 'NOT_CERTIFIED',       // No halal certificate
  EXPIRED = 'EXPIRED',                   // Certificate expired
  PENDING_VERIFICATION = 'PENDING_VERIFICATION', // Certificate under verification
}

/**
 * Supplier information
 */
export interface Supplier {
  id: string;
  companyId: string; // Company that added this supplier
  name: string;
  type: SupplierType;
  status: SupplierStatus;

  // Contact information
  contactPerson: ContactPerson;
  address: Address;
  website?: string;

  // Halal certification
  halalCertified: boolean;
  certificationStatus: HalalCertificationStatus;
  certificationNumber?: string;
  certificationBody?: string;
  certificationIssueDate?: Date;
  certificationExpiryDate?: Date;
  certificateUrl?: string;

  // Products/Services
  productsProvided: string[]; // List of products/services
  materialCategories: string[]; // Categories of materials

  // Risk assessment
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
  lastAuditDate?: Date;
  nextAuditDate?: Date;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy?: string;
}

/**
 * Ingredient/Material
 */
export interface Ingredient {
  id: string;
  supplierId: string;
  companyId: string; // Company using this ingredient
  name: string;
  description?: string;

  // Classification
  category: string; // e.g., "Dairy", "Meat", "Vegetable", "Chemical"
  type: string; // More specific type

  // Halal status
  halalStatus: 'HALAL' | 'NON_HALAL' | 'MUSHBOOH' | 'PENDING_VERIFICATION';
  statusNotes?: string;

  // Certification
  certificateNumber?: string;
  certificateUrl?: string;
  certificationExpiryDate?: Date;

  // Usage
  usedInProducts: string[]; // Products that use this ingredient
  monthlyUsageQuantity?: number;
  unit?: string; // kg, L, etc.

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Supplier audit record
 */
export interface SupplierAudit {
  id: string;
  supplierId: string;
  auditDate: Date;
  auditorName: string;
  auditType: 'INITIAL' | 'SURVEILLANCE' | 'SPECIAL';
  auditScore: number; // 0-100
  findings: string[];
  recommendations: string[];
  nextAuditDate: Date;
  reportUrl?: string;
}

/**
 * Helper to check if supplier certificate is valid
 */
export function isSupplierCertificateValid(supplier: Supplier): boolean {
  if (!supplier.halalCertified || !supplier.certificationExpiryDate) {
    return false;
  }
  return new Date(supplier.certificationExpiryDate) > new Date();
}

/**
 * Helper to get days until certificate expiry
 */
export function getDaysUntilExpiry(expiryDate: Date): number {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Helper to check if certificate is expiring soon (within 30 days)
 */
export function isCertificateExpiringSoon(supplier: Supplier): boolean {
  if (!supplier.certificationExpiryDate) return false;
  const daysUntilExpiry = getDaysUntilExpiry(supplier.certificationExpiryDate);
  return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
}
